import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_96339eda-de9f-4afd-b0cc-aa940ad2aaf2/artifacts/doc4booq_SubGuard_icon.png" 
                  alt="SubGuard" 
                  className="w-8 h-8 rounded-lg"
                />
                <span className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
                  SubGuard
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
          Privacy Policy
        </h1>
        <p className="text-gray-500 mb-8">Last updated: January 2026</p>

        <div className="prose prose-gray max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-600 mb-4">
              SubGuard ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our subscription management service at subguardhq.com.
            </p>
            <p className="text-gray-600">
              By using SubGuard, you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">2.1 Account Information</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Email address (for account creation and login)</li>
              <li>Name (if provided via Google sign-in)</li>
              <li>Profile picture (if provided via Google sign-in)</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">2.2 Email Data (Gmail Integration)</h3>
            <p className="text-gray-600 mb-4">
              When you connect your Gmail account, we access your emails in <strong>read-only mode</strong> to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Scan for subscription-related emails (receipts, renewal notices, confirmations)</li>
              <li>Extract subscription metadata (service name, amount, billing frequency)</li>
            </ul>
            <div className="bg-[#D1FAE5] border border-[#064E3B]/20 rounded-xl p-4 mt-4">
              <p className="text-[#064E3B] font-medium">
                <strong>Important:</strong> We do NOT store the content of your emails. We only extract and store subscription-related metadata. We cannot send, delete, or modify your emails.
              </p>
            </div>

            <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">2.3 Subscription Data</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Subscription names and service providers</li>
              <li>Billing amounts and frequencies</li>
              <li>Renewal dates</li>
              <li>Subscription status (active, cancelled)</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">2.4 Payment Information</h3>
            <p className="text-gray-600">
              Payment processing is handled by Stripe. We do not store your credit card details. We only receive confirmation of successful payments and your Stripe customer ID.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>To provide and maintain our subscription tracking service</li>
              <li>To detect and identify your subscriptions from email data</li>
              <li>To send you renewal alerts and notifications</li>
              <li>To process payments and manage your account</li>
              <li>To provide AI-powered cancellation assistance</li>
              <li>To improve and personalize our service</li>
              <li>To communicate with you about service updates</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Data Sharing and Disclosure</h2>
            <p className="text-gray-600 mb-4">We do NOT sell your personal information. We may share data with:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Service Providers:</strong> Stripe (payments), Brevo (transactional emails), MongoDB Atlas (database hosting)</li>
              <li><strong>AI Services:</strong> We use AI to analyze subscription data and generate cancellation instructions. Subscription metadata may be processed by AI providers.</li>
              <li><strong>Legal Requirements:</strong> If required by law or to protect our rights</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
            <p className="text-gray-600 mb-4">We implement appropriate security measures including:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Encrypted data transmission (HTTPS/TLS)</li>
              <li>Secure database storage with MongoDB Atlas</li>
              <li>OAuth 2.0 for Gmail authentication</li>
              <li>JWT tokens for session management</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
            <p className="text-gray-600">
              We retain your account and subscription data for as long as your account is active. You can request deletion of your account and associated data at any time by contacting us at hello@subguardhq.com.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Your Rights</h2>
            <p className="text-gray-600 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Delete your account and data</li>
              <li>Disconnect your Gmail account at any time</li>
              <li>Export your subscription data</li>
              <li>Opt out of marketing communications</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Gmail API Usage</h2>
            <p className="text-gray-600 mb-4">
              Our use of information received from Google APIs adheres to the <a href="https://developers.google.com/terms/api-services-user-data-policy" className="text-[#064E3B] underline" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.
            </p>
            <p className="text-gray-600">
              We only request the minimum necessary permissions (gmail.readonly) and use this access solely for detecting subscription-related emails.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Cookies and Tracking</h2>
            <p className="text-gray-600 mb-4">We use:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Essential Cookies:</strong> For authentication and session management</li>
              <li><strong>Analytics:</strong> Google Analytics to understand how users interact with our service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Children's Privacy</h2>
            <p className="text-gray-600">
              SubGuard is not intended for users under 18 years of age. We do not knowingly collect personal information from children.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Changes to This Policy</h2>
            <p className="text-gray-600">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Contact Us</h2>
            <p className="text-gray-600">
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <p className="text-gray-900 font-medium mt-2">
              Email: hello@subguardhq.com
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-sm">© {new Date().getFullYear()} SubGuard. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
